import React, { useEffect, useState } from "react";
import { headers, restApiUrl } from "./app_props";
// no longer using direct links on dashboard
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  RadialLinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Line, Doughnut, Bar, PolarArea } from "react-chartjs-2";
import { Select, MenuItem, FormControl, InputLabel } from "@mui/material";

ChartJS.register(
  CategoryScale,
  LinearScale,
  RadialLinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
);

const Dashboard = () => {
  const header = headers();
  const [financialYears, setFinancialYears] = useState([]);
  const [selectedFY, setSelectedFY] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");
  const user = JSON.parse(sessionStorage.getItem("userInfo"));
  const sites = JSON.parse(user.sites);
  const [selectedSite, setSelectedSite] = useState("");
  const [charts, setCharts] = useState(null);
  const [residualRiskLevels, setResidualRiskLevels] = useState([]);

  const decodeToken = (token) => {
    try {
      const payload = token.split(".")[1];
      const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
      return JSON.parse(json);
    } catch (e) {
      return null;
    }
  };

  const buildFinancialYears = () => {
    const now = new Date();
    const year = now.getMonth() + 1 >= 4 ? now.getFullYear() : now.getFullYear() - 1; // FY start year
    const yrs = [];
    for (let i = 0; i < 10; i++) {
      const s = year - i;
      yrs.push(`${s}-${s + 1}`);
    }
    setFinancialYears(yrs);
  };

  const fetchCharts = (params = {}) => {
    const url = new URL(`${restApiUrl}/api/dashboard/charts`);
    Object.keys(params).forEach((k) => {
      if (params[k] !== undefined && params[k] !== null && params[k] !== "") url.searchParams.set(k, params[k]);
    });
    fetch(url.toString(), { method: "GET", mode: "cors", headers: header })
      .then((r) => r.json())
      .then((c) => {
        if (c && c.success) setCharts(c.data);
        if (c && c.success && c.data) {
          const levels = c.data.residualRiskLevels || c.data.residualRiskLabels || (c.data.residualRiskByPermit ? Object.keys(c.data.residualRiskByPermit) : null);
          if (levels && Array.isArray(levels)) setResidualRiskLevels(levels);
        }
      })
      .catch(() => { });
  };

  useEffect(() => {
    buildFinancialYears();
    fetchCharts({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // trigger reload when filters change
    const params = {};
    if (selectedFY) params.financialYear = selectedFY;
    if (selectedMonth) params.month = selectedMonth; // numeric month expected by backend
    if (selectedSite) params.siteId = selectedSite;
    fetchCharts(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFY, selectedMonth, selectedSite]);

  // map backend charts when available
  const statusCounts = charts && charts.statusCounts ? charts.statusCounts : {};
  const statusLabels = Object.keys(statusCounts);
  const statusData = statusLabels.map((l) => statusCounts[l]);

  const doughnutLabels = ["Active", "Completed", "Overdue"];
  const doughnutDataValues = [
    charts ? charts.summary.activePermits || 0 : 0,
    charts && charts.kpi
      ? charts.kpi.completedPermits || 0
      : 0,
    charts && charts.kpi
      ? charts.kpi.overduePermits || 0
      : 0,
  ];

  const monthlyCreated =
    charts && charts.monthlyCreated ? charts.monthlyCreated : {};
  const monthLabels = Object.keys(monthlyCreated);
  const monthData = monthLabels.map((k) => monthlyCreated[k]);

  // residual risk: build labels and before/after datasets with fallbacks
  const riskLabels = (charts && charts.residualRiskLabels) ||
    (charts && charts.riskLabels) || ["Low", "Medium", "High", "Very High"];

  const riskData = riskLabels.map((label) => {
    if (charts && charts.residualRisk && charts.residualRisk[label] != null)
      return charts.residualRisk[label];
    if (
      charts &&
      charts.residualRiskBefore &&
      charts.residualRiskBefore[label] != null
    )
      return charts.residualRiskBefore[label];
    if (
      charts &&
      charts.residualRiskAfter &&
      charts.residualRiskAfter[label] != null
    )
      return charts.residualRiskAfter[label];
    return 0;
  });

  // Residual risk preferred labels
  const getMapValue = (obj, key) => {
    if (!obj) return undefined;
    return (
      obj[key] ??
      obj[key.toUpperCase()] ??
      obj[key.toLowerCase()] ??
      obj[String(key).toLowerCase()]
    );
  };

  const residualCounts = residualRiskLevels.map((lvl) => {
    const byPermit = getMapValue(charts && charts.residualRiskByPermit, lvl);
    if (byPermit != null) return byPermit;
    const val = getMapValue(charts && charts.residualRisk, lvl);
    if (val != null) return val;
    // fallback to array
    const idx = riskLabels.findIndex(
      (r) => r.toLowerCase() === lvl.toLowerCase(),
    );
    return idx >= 0 ? riskData[idx] || 0 : 0;
  });

  // Overall risk labels: prefer explicit keys from charts.overallRisk when available
  // and sort them from Very Low -> Very High when possible
  const preferredOrder = ["Very Low", "Low", "Medium", "High", "Very High"];
  const rawOverallLabels = charts && charts.overallRisk
    ? Object.keys(charts.overallRisk)
    : ["Low", "Medium", "High"];
  const overallLevelLabels = rawOverallLabels.slice().sort((a, b) => {
    const ia = preferredOrder.findIndex((p) => p.toLowerCase() === String(a).toLowerCase());
    const ib = preferredOrder.findIndex((p) => p.toLowerCase() === String(b).toLowerCase());
    if (ia >= 0 && ib >= 0) return ia - ib;
    if (ia >= 0) return -1;
    if (ib >= 0) return 1;
    return String(a).localeCompare(String(b));
  });
  const overallCounts = overallLevelLabels.map((lvl) => {
    // Prefer explicit overallRisk mapping from the backend when present
    const fromOverall = getMapValue(charts && charts.overallRisk, lvl);
    if (fromOverall != null) return fromOverall;
    // Fallback to residualRisk map (legacy behavior)
    const fromMap = getMapValue(charts && charts.residualRisk, lvl);
    if (fromMap != null) return fromMap;
    // Final fallback: map via riskLabels/riskData array
    const idx = riskLabels.findIndex(
      (r) => r.toLowerCase() === lvl.toLowerCase(),
    );
    return idx >= 0 ? riskData[idx] || 0 : 0;
  });

  // support simple overall numeric before/after fallback
  let showOverallPair = false;
  let overallBefore = 0;
  let overallAfter = 0;
  if (charts && typeof charts.overallRisk === "number") {
    showOverallPair = true;
    overallBefore = charts.overallRisk; // show single value
  }

  // completion rate for polar chart
  const completionRate =
    charts && charts.kpi && charts.kpi.completionRate
      ? Math.round(charts.kpi.completionRate)
      : 0;
  const polarData = [completionRate, Math.max(0, 100 - completionRate)];

  return (
    <div style={{ height: "100%" }}>
      <div className="m-2 flex flex-wrap gap-2 items-center">
        <FormControl size="small" variant="outlined" style={{ minWidth: 130 }}>
          <InputLabel id="year-select-label">Year</InputLabel>
          <Select labelId="year-select-label" label="Year" value={selectedFY}
            onChange={(e) => setSelectedFY(e.target.value)}>
            <MenuItem value="">All Financial Years</MenuItem>
            {financialYears.map((fy) => <MenuItem key={fy} value={fy}>{fy}</MenuItem>)}
          </Select>
        </FormControl>

        <FormControl size="small" variant="outlined" style={{ minWidth: 130 }}>
          <InputLabel id="month-select-label">Month</InputLabel>
          <Select labelId="month-select-label" label="Month" value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}>
            <MenuItem value="">All Months</MenuItem>
            {["January","February","March","April","May","June","July","August","September","October","November","December"]
              .map((m, i) => <MenuItem key={i+1} value={String(i+1)}>{m}</MenuItem>)}
          </Select>
        </FormControl>

        <FormControl size="small" variant="outlined" style={{ minWidth: 130 }}>
          <InputLabel id="site-select-label">Site</InputLabel>
          <Select labelId="site-select-label" label="Site" value={selectedSite}
            onChange={(e) => setSelectedSite(e.target.value)}>
            <MenuItem value="">All Sites</MenuItem>
            {sites.map((s) => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
          </Select>
        </FormControl>
      </div>
      <div
        className="dashboard m-2"
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          gap: "1rem",
        }}
      >
        <div
          className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-2"
          style={{ gridAutoRows: "1fr" }}
        >
          <div className="bg-white p-4 rounded-lg shadow-md flex-1 flex flex-col min-h-0">
            <h3 className="text-lg font-semibold mb-2">Permits by Status</h3>
            <div className="flex-1">
              <Bar
                data={{
                  labels: statusLabels,
                  datasets: [
                    {
                      label: "Permits",
                      data: statusData,
                      backgroundColor: "rgba(59,130,246,0.85)",
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: { legend: { display: false } },
                }}
              />
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg shadow-md flex-1 flex flex-col min-h-0">
            <h3 className="text-lg font-semibold mb-2">
              Active / Completed / Overdue
            </h3>
            <div className="flex-1 flex items-center justify-center">
              <Doughnut
                data={{
                  labels: doughnutLabels,
                  datasets: [
                    {
                      data: doughnutDataValues,
                      backgroundColor: ["#10B981", "#6366F1", "#F59E0B"],
                    },
                  ],
                }}
                options={{ responsive: true, maintainAspectRatio: false }}
              />
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg shadow-md flex-1 flex flex-col min-h-0">
            <h3 className="text-lg font-semibold mb-2">
              Permits Scheduled (last 6 months)
            </h3>
            <div className="flex-1">
              <Line
                data={{
                  labels: monthLabels,
                  datasets: [
                    {
                      label: "Scheduled",
                      data: monthData,
                      borderColor: "rgba(99,102,241,0.95)",
                      backgroundColor: "rgba(99,102,241,0.15)",
                      tension: 0.3,
                      fill: true,
                    },
                  ],
                }}
                options={{ responsive: true, maintainAspectRatio: false }}
              />
            </div>
          </div>
        </div>

        <div
          className="grid grid-cols-1 md:grid-cols-3 gap-4"
          style={{ gridAutoRows: "1fr", flex: 1 }}
        >
          <div className="bg-white p-4 rounded-lg shadow-md flex-1 flex flex-col min-h-0">
            <h3 className="text-lg font-semibold mb-2">Completion Rate</h3>
            <div className="flex-1 flex items-center justify-center">
              <PolarArea
                data={{
                  labels: ["Completed %", "Remaining %"],
                  datasets: [
                    {
                      data: polarData,
                      backgroundColor: ["#10B981", "#E5E7EB"],
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: { legend: { position: "bottom" } },
                }}
              />
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg shadow-md flex-1 flex flex-col min-h-0">
            <h3 className="text-lg font-semibold mb-2">
              Residual Risk Level Counts
            </h3>
            <p className="text-sm text-gray-500 mb-2">
              Counts by level ({(residualRiskLevels && residualRiskLevels.length) ? residualRiskLevels.join(', ') : 'Low, Medium, High, Critical'}).
            </p>
            <div className="flex-1">
              <Bar
                data={{
                  labels: residualRiskLevels,
                  datasets: [
                    {
                      label: "Count",
                      data: residualCounts || [],
                      backgroundColor: [
                        "#10B981",
                        "#6366F1",
                        "#F97316",
                        "#EF4444",
                      ],
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: { legend: { display: false } },
                  scales: { y: { beginAtZero: true } },
                }}
              />
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg shadow-md flex-1 flex flex-col min-h-0">
            <h3 className="text-lg font-semibold mb-2">Overall Risk</h3>
            <p className="text-sm text-gray-500 mb-2">
              Counts by level ({(overallLevelLabels && overallLevelLabels.length) ? overallLevelLabels.join(', ') : 'Low, Medium, High'}).
            </p>
            <div className="flex-1">
              <Bar
                data={{
                  labels: overallLevelLabels,
                  datasets: [
                    {
                      label: "Overall",
                      data: overallCounts,
                      backgroundColor: "rgba(59,130,246,0.85)",
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: { legend: { position: "bottom" } },
                  scales: { y: { beginAtZero: true } },
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
